import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TabInfo {
  widgetId: string;
  title: string;
  isInput?: boolean;
}

interface TabGroupContainerProps {
  tabs: TabInfo[];
  activeWidgetId?: string;
  isEditable: boolean;
  hideMaximize?: boolean;
  renderWidget: (widgetId: string) => React.ReactNode;
  onActiveTabChange?: (widgetId: string) => void;
}

const TabGroupContainer: React.FC<TabGroupContainerProps> = ({
  tabs,
  activeWidgetId,
  isEditable,
  hideMaximize,
  renderWidget,
  onActiveTabChange,
}) => {
  const [localActiveId, setLocalActiveId] = useState(activeWidgetId || tabs[0]?.widgetId);
  const [hasUserClicked, setHasUserClicked] = useState(false);
  const [maximized, setMaximized] = useState(false);

  const currentActiveId = hasUserClicked ? localActiveId : (activeWidgetId || localActiveId);

  const handleTabClick = useCallback((widgetId: string) => {
    setHasUserClicked(true);
    setLocalActiveId(widgetId);
    onActiveTabChange?.(widgetId);
  }, [onActiveTabChange]);

  // ESC to close
  useEffect(() => {
    if (!maximized) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMaximized(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [maximized]);

  if (tabs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-neutral-400 text-sm">
        Empty tab group
      </div>
    );
  }

  const activeTab = tabs.find(t => t.widgetId === currentActiveId);
  const activeIsInput = activeTab?.isInput ?? false;

  const tabBar = (
    <div className="tab-group-tabs tab-group-tabs--display flex-shrink-0 flex items-center border-b px-1 gap-0.5 overflow-x-auto">
      <div className="flex items-center gap-0.5 flex-1 min-w-0">
        {tabs.map((tab) => {
          const isActive = tab.widgetId === currentActiveId;
          return (
            <div key={tab.widgetId} className="flex items-center group flex-shrink-0">
              <button
                onClick={() => handleTabClick(tab.widgetId)}
                className={`
                  px-3 py-1.5 text-xs font-medium rounded-t-md transition-all
                  inline-flex items-center gap-1.5
                  ${isActive
                    ? 'bg-surface text-neutral-900 shadow-sm border border-neutral-200 border-b-surface -mb-px z-10'
                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 border border-transparent'
                  }
                `}
              >

                <span className="truncate max-w-[120px]">{tab.title}</span>
              </button>
            </div>
          );
        })}
      </div>
      {!isEditable && !hideMaximize && (
        <button
          onClick={(e) => { e.stopPropagation(); setMaximized(true); }}
          className="p-0.5 rounded hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-colors flex-shrink-0"
          title="Maximize"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
          </svg>
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className="tab-group-container h-full flex flex-col">
        {tabBar}
        {/* Active tab content */}
        <div className="tab-group-content flex-1 overflow-auto min-h-0">
          {currentActiveId && renderWidget(currentActiveId)}
        </div>
      </div>

      {/* Maximized fullscreen overlay */}
      {maximized && createPortal(
        <div className="fixed inset-0 z-50 bg-surface flex flex-col">
          {/* Header with tabs */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-200 bg-neutral-50 flex-shrink-0">
            <div className="flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto">
              {tabs.map((tab) => {
                const isActive = tab.widgetId === currentActiveId;
                return (
                  <button
                    key={tab.widgetId}
                    onClick={() => handleTabClick(tab.widgetId)}
                    className={`
                      px-3 py-1.5 text-sm font-medium rounded-lg transition-all
                      inline-flex items-center gap-1.5 flex-shrink-0
                      ${isActive
                        ? 'bg-surface text-neutral-900 shadow-sm border border-neutral-200'
                        : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                      }
                    `}
                  >

                    <span>{tab.title}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setMaximized(false)}
              className="p-1.5 rounded-lg hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700 transition-colors"
              title="Close (Esc)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-auto min-h-0 p-4">
            {currentActiveId && renderWidget(currentActiveId)}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default React.memo(TabGroupContainer);
